import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Banknote, User, Users2, Search } from 'lucide-react';
import api, { fmtIDR, fmtDate, currentMonth } from '@/lib/api';
import { toast } from 'sonner';
import { empCatMeta, EMPLOYEE_CATEGORIES } from '@/lib/expenseCategories';

export default function OwnerSalary() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [month, setMonth] = useState(currentMonth());

  const load = useCallback(async () => {
    try {
      const [emps, hist] = await Promise.all([
        api.get('/employees'),
        api.get(`/cash/movements?kind=out&month=${month}&category=gaji_karyawan`),
      ]);
      setEmployees(emps.data);
      setHistory(hist.data);
    } catch (e) {
      console.error('[OwnerSalary] gagal load:', e);
      toast.error(e?.response?.data?.detail || 'Gagal memuat');
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const selectedEmp = useMemo(() => employees.find((e) => e.id === employeeId), [employees, employeeId]);
  const totalMonth = useMemo(() => history.reduce((a, h) => a + (h.amount || 0), 0), [history]);

  const submit = async (e) => {
    e.preventDefault();
    if (!employeeId) return toast.error('Pilih karyawan terlebih dahulu');
    const n = Number(amount);
    if (!n || n <= 0) return toast.error('Jumlah gaji tidak valid');
    if (!selectedEmp) return toast.error('Data karyawan tidak ditemukan');
    setSubmitting(true);
    try {
      const { data } = await api.post('/cash/expenses', {
        category: 'gaji_karyawan',
        recipient_name: selectedEmp.name,
        amount: n,
        description: description.trim() || `Gaji ${selectedEmp.name} (${empCatMeta(selectedEmp.category).label})`,
        employee_id: selectedEmp.id,
      });
      toast.success(`Gaji ${selectedEmp.name} tercatat. Saldo kas: ${fmtIDR(data.balance)}`);
      setAmount(''); setDescription('');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gagal simpan'); }
    finally { setSubmitting(false); }
  };

  const grouped = useMemo(() => {
    const map = {};
    EMPLOYEE_CATEGORIES.forEach((c) => { map[c.value] = { count: 0, total: 0 }; });
    history.forEach((h) => {
      const emp = employees.find((e) => e.id === h.employee_id);
      const cat = emp?.category || 'office';
      if (!map[cat]) map[cat] = { count: 0, total: 0 };
      map[cat].count += 1;
      map[cat].total += h.amount;
    });
    return map;
  }, [history, employees]);

  return (
    <AppShell nav={ownerNav} title="Input Gaji Karyawan" showBell>
      <div className="space-y-4">
        <div className="rounded-lg border bg-sky-50 p-3 text-xs text-sky-900">
          <b>Info:</b> Gaji yang diinput otomatis tercatat sebagai pengeluaran (kategori <b>Gaji Karyawan</b>) di modul Pengeluaran dan otomatis mengurangi <b>Laba Bersih</b> perusahaan di modul Laporan.
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Form input gaji */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Banknote className="h-4 w-4 text-blue-600" />Input Gaji</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Karyawan <span className="text-rose-500">*</span></Label>
                  <Select value={employeeId} onValueChange={setEmployeeId}>
                    <SelectTrigger data-testid="salary-employee"><SelectValue placeholder={employees.length ? 'Pilih karyawan...' : 'Belum ada karyawan terdaftar'} /></SelectTrigger>
                    <SelectContent>
                      {employees.length === 0 ? (
                        <div className="text-xs text-muted-foreground p-3">Daftarkan karyawan dulu di menu "Daftar Karyawan"</div>
                      ) : employees.map((e) => {
                        const em = empCatMeta(e.category);
                        return (
                          <SelectItem key={e.id} value={e.id} data-testid={`salary-employee-${e.id}`}>
                            <div className="flex items-center gap-2">
                              <em.icon className="h-3.5 w-3.5" />
                              {e.name} <span className="text-xs text-muted-foreground">({em.label})</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {selectedEmp && (
                    <p className="text-[10px] text-muted-foreground">Kategori: <b>{empCatMeta(selectedEmp.category).label}</b> {selectedEmp.phone ? `• ${selectedEmp.phone}` : ''}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Besar Gaji (Rupiah) <span className="text-rose-500">*</span></Label>
                  <Input type="number" min="1" step="1" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="mis. 3500000" data-testid="salary-amount" />
                </div>
                <div className="space-y-1.5">
                  <Label>Catatan (opsional)</Label>
                  <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="mis. Gaji bulan Juli 2026, THR, bonus..." data-testid="salary-description" />
                </div>
                <Button type="submit" disabled={submitting || !employeeId} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="salary-submit">
                  {submitting ? 'Menyimpan...' : 'Bayar Gaji'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Ringkasan bulan */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users2 className="h-4 w-4" />Ringkasan Bulan Ini
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Total Gaji Terbayar</div>
                  <div className="text-2xl font-heading font-semibold tabular-nums text-blue-700" data-testid="salary-total">{fmtIDR(totalMonth)}</div>
                </div>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="salary-month" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {EMPLOYEE_CATEGORIES.map((c) => {
                  const g = grouped[c.value] || { count: 0, total: 0 };
                  return (
                    <div key={c.value} className={`rounded-lg border p-2 ${c.color}`}>
                      <div className="flex items-center gap-1.5 text-xs"><c.icon className="h-3.5 w-3.5" />{c.label}</div>
                      <div className="text-sm font-semibold tabular-nums mt-1">{fmtIDR(g.total)}</div>
                      <div className="text-[10px] text-muted-foreground">{g.count} pembayaran</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* History */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Riwayat Gaji ({history.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {history.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">Belum ada pembayaran gaji bulan ini.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="salary-history">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tanggal</TableHead>
                      <TableHead>Karyawan</TableHead>
                      <TableHead>Catatan</TableHead>
                      <TableHead>Dibayar Oleh</TableHead>
                      <TableHead className="text-right">Nominal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h) => {
                      const emp = employees.find((e) => e.id === h.employee_id);
                      const em = emp ? empCatMeta(emp.category) : null;
                      return (
                        <TableRow key={h.id} data-testid={`salary-history-${h.id}`}>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(h.created_at)}</TableCell>
                          <TableCell>
                            <div className="font-medium">{h.recipient_name}</div>
                            {em && <Badge className={`${em.color} border-0 mt-1 text-[10px]`}><em.icon className="h-2.5 w-2.5 mr-1" />{em.label}</Badge>}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{h.description || '-'}</TableCell>
                          <TableCell className="text-xs">{h.created_by_name}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold text-blue-700">-{fmtIDR(h.amount)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
